import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {BaseComponent} from '../../core/base.component';
import {AuthService} from '../../core/security/auth.service';
import {RolesEnum} from '../../core/security/roles.enum';
import {NotificationService} from '../../notification/notification.service';
import {ContactService} from '../contact.service';
import {DuplicateContactSummaryModel} from '../../api/models/duplicate-contact-summary-model';
import {MatDialog} from '@angular/material/dialog';
import {DuplicatesDialogComponent} from '../duplicates/duplicates-dialog.component';
import {ContactsGridComponent} from './contacts-grid.component';
import {ContactListFilterDialogComponent} from '../list-filter/contact-list-filter-dialog.component';
import {ContactsBulkEditComponent} from '../bulk-edit/bulk-edit.component';
import {FilterOptionModel} from '../../dialogs/grid-filter/filter-option.model';
import {SelectedFilterValuesModel} from '../../dialogs/grid-filter/selected-filter-values.model';
import {ContactsRequest} from '../../api/models/contacts-request';
import {GridFilterComponent} from '../../dialogs/grid-filter/grid-filter.component';
import { FlexModule } from '@angular/flex-layout';
import { FilterRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-row-additional-content.component';
import { NgIf, NgFor } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { FilterSecondRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-second-row-additional-content.component';

@Component({
    selector: 'app-contacts-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        GridFilterComponent,
        FilterRowAdditionalContentComponent,
        NgIf,
        MatButton,
        MatTooltip,
        FilterSecondRowAdditionalContentComponent,
        NgFor,
        ContactsGridComponent,
    ],
})
export class ContactListComponent extends BaseComponent implements OnInit, OnDestroy {

  @Output() filterChangeEvent = new EventEmitter<any>();
  filterOptions: FilterOptionModel[] = [];
  selectedFilter: SelectedFilterValuesModel[] = new Array<SelectedFilterValuesModel>();
  selectedFilterChange = new EventEmitter<SelectedFilterValuesModel[]>();

  duplicateSummary = <DuplicateContactSummaryModel[]>[];
  @ViewChild('contactsGrid', {static: true}) contactsGrid: ContactsGridComponent;
  @ViewChild('gridFilter', {static: true}) gridFilter: GridFilterComponent;
  detailsUrl: string;

  @Input() readonly = false;
  @Input() disableUrlChanges = false;
  @Input() customContactsQuery = '';

  constructor(route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService,
              public contactService: ContactService,
              private router: Router,
              public dialog: MatDialog,
              private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);
    this.detailsUrl = '../contact';

    this.editRoles = [RolesEnum.CompanyContact_Edit, RolesEnum.CompanyContact_Delete,
      RolesEnum.PrivateContact_Edit, RolesEnum.PrivateContact_Delete];
    this.deleteRoles = [RolesEnum.CompanyContact_Delete, RolesEnum.PrivateContact_Delete];

    this.headerService.hideSearchInput();
  }

  ngOnInit() {
    super.ngOnInit();

    this.asyncInit().then(() => {
      console.log('async init complete');
    });
  }

  async asyncInit() {
    this.filterOptions = await this.contactService.initFilterOptions();

    if (!this.readonly) {
      await this.getDuplicateContactsSummary();
    }
  }

  navigateToDetails() {
    this.router.navigate([this.detailsUrl, 'new', 'edit'], {relativeTo: this.route});
  }

  async getDuplicateContactsSummary() {
    try {
      this.duplicateSummary = await this.contactService.getDuplicateUserContactsSummary();
    } catch (e) {
      console.error(e);
      this.duplicateSummary = [];
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.hideSearchInput();
    this.headerService.searchText.next('');
  }

  openFilterDialog() {
    console.log('open filter dialog');
    const dialogRef = this.dialog.open(ContactListFilterDialogComponent, {
      width: '500px',
      height: '90%',
      data: {
        filter: this.selectedFilter,
      }
    });

    dialogRef.afterClosed().subscribe(async (result: SelectedFilterValuesModel[]) => {
      if (result) {
        this.selectedFilterChange.emit(result);
      }
    });
  }

  openDuplicatesList($event: MouseEvent, recordType: string) {
    console.log($event, recordType);
    const dialogRef = this.dialog.open(DuplicatesDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        contactType: recordType,
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      this.contactsGrid.loadData();
      await this.getDuplicateContactsSummary();
    });
  }

  onFilterChanged(filter: any) {
    this.filterChangeEvent.emit(filter);
  }

  onBulkEditClick() {
    const ids = this.contactsGrid.selectedRows.selected.map(x => x.id);

    if (ids.length === 0) {
      this.notificationService.notifyDanger('Select at least one contact to edit');
      return;
    }

    const currentFilter: any = this.getDataFilter();

    const dialogRef = this.dialog.open(ContactsBulkEditComponent, {
      width: '400px',
      data: {
        ids: ids,
        totalContacts: this.contactsGrid.paginator.length,
        filter: currentFilter
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === 'success') {
        this.contactsGrid.selectedRows.clear();
        this.filterChangeEvent.emit(currentFilter);
      }
    });
  }

  onSelectedItemsChanged($event: SelectedFilterValuesModel[]) {
    this.selectedFilter = $event;
  }

  getDataFilter(): ContactsRequest {
    const filter = this.contactsGrid.getDataFilter();
    filter.filterQuery = this.gridFilter.currentQuery;

    return filter;
  }
}
